import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App.js';
import { AuthBootstrap } from './auth/AuthBootstrap.js';
import { tenantScopedHashFn } from './auth/active-tenant.js';
import { SharedReportViewer } from './pages/share/SharedReportViewer.js';
import { SharedPresentationViewer } from './pages/share/SharedPresentationViewer.js';
import './styles/canopy-tokens.generated.css';
import './styles/base.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
      // Partition the cache by active tenant id so cross-tenant
      // session switches never serve stale data from the previous
      // tenant. The hash function reads a module-level variable
      // (active-tenant.ts) populated by AuthBootstrap from
      // /me/tenants; queries fired before that resolves get a `_`
      // prefix and re-fetch cleanly when the real id lands.
      queryKeyHashFn: tenantScopedHashFn,
    },
  },
});

/**
 * CSAGE-396: debug handle on the query cache.
 *
 * The Insights nav renders "Loading…" whenever a list query is pending, and
 * that string is the ONLY thing it says — a pending query, a cancelled one
 * and an orphaned one all look identical from outside. When the dashboards
 * panel hung in UAT, the server request log proved the call returned 200
 * with the right scope and role, and there was still no way to ask the
 * client which cache entry it was waiting on or what state that entry was
 * in. Everything downstream of the response was a black box.
 *
 * Exposing the client closes that. It grants no capability — anything able
 * to run script in this origin already has the cache — but it turns
 * "it says Loading" into a precise question:
 *
 *   __sageQueryClient.getQueryCache().getAll()
 *     .map(q => ({ hash: q.queryHash, status: q.state.status,
 *                  fetchStatus: q.state.fetchStatus,
 *                  observers: q.getObserversCount() }))
 *
 * which shows the entry, its address, whether anyone is subscribed to it,
 * and whether a fetch is actually in flight.
 *
 * Non-production only. The handle grants no capability — anything running
 * script in this origin already reaches the cache — so this is hygiene, not
 * a vulnerability fix: a debugging affordance should not be a permanent
 * fixture of the production bundle.
 *
 * Gated at RUNTIME on the hostname rather than at build time, because one
 * bundle serves every environment (`resolveApiOrigin` derives the API host
 * the same way). `sage.gocanopy.io` is production; `sage-dev` / `sage-uat` /
 * `sage-demo` and localhost keep the handle, which is where diagnosis
 * actually happens.
 */
const IS_PRODUCTION_HOST =
  typeof window !== 'undefined' && window.location.hostname === 'sage.gocanopy.io';
if (!IS_PRODUCTION_HOST) {
  (globalThis as unknown as { __sageQueryClient?: QueryClient }).__sageQueryClient = queryClient;
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

// Router basename mirrors the SPA's mount path. Vite's `base` (set in
// vite.config.ts) drives both: in dev / dedicated-subdomain deploys
// (sage.gocanopy.io) we serve at '/', in shared-domain deploys we
// serve at '/reporting/app/'. Read the same env var the api client
// already does.
const ROUTER_BASENAME =
  ((import.meta as unknown as { env?: Record<string, string> }).env?.['BASE_URL'] ?? '/').replace(
    /\/+$/u,
    '',
  ) || '/';

/**
 * Top-level routing: the shared report viewer at `/r/:token` is
 * intentionally MOUNTED OUTSIDE `<AuthBootstrap onTokenChanged={() => void queryClient.invalidateQueries()}>` and `<App>`. It is
 * a public viewer — anyone with the link can load it, no JWT needed,
 * no Sage chrome around the report. Every other path goes through
 * the authenticated SPA shell as before.
 */
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={ROUTER_BASENAME}>
        <Routes>
          <Route path="/r/:token" element={<SharedReportViewer />} />
          <Route path="/p/:id/:token" element={<SharedPresentationViewer />} />
          <Route
            path="/*"
            element={
              <AuthBootstrap onTokenChanged={() => void queryClient.invalidateQueries()}>
                <App />
              </AuthBootstrap>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
